import type { IProduct } from '~/modules/catalog/types/product';
import { useVisitorStore } from '~/modules/visitors/stores/visitorStore';
import { useToastStore } from '~/stores/toastStore';
import { useMindbox } from '~/uses/useMindbox';

// todo AbortController
export const useFavorites = () => {

  const { $api } = useNuxtApp();

  const visitorStore = useVisitorStore();
  const toastStore = useToastStore();
  const { setWishList } = useMindbox();

  const pendingRequests = new Map<number, Promise<boolean>>();

  const syncMindboxWishlist = () => {
    const items = visitorStore.favoriteItems.map((product: IProduct) => {
      const firstGroup = product.group_products?.[0];
      return {
        productId: firstGroup?.external_key ?? product.external_key ?? firstGroup?.id ?? product.id,
        price: Number(firstGroup?.final_price?.amount ?? 0),
      };
    });
    setWishList(items);
  };

  const _executeFavoriteAction = async (
    action: 'add' | 'remove',
    visitorID: number,
    itemID: number,
  ): Promise<boolean> => {

    if (pendingRequests.has(itemID)) {
      return pendingRequests.get(itemID)!;
    }

    visitorStore.toggleFavoriteList(itemID);

    const requestPromise = (async () => {
      try {
        const { success, message } = await (
          action === 'add'
            ? $api.favorites.addFavorite(visitorID, itemID)
            : $api.favorites.removeFavorite(visitorID, itemID)
        );

        const toastText = action === 'add'
          ? 'Товар добавлен в избранное'
          : 'Товар удалён из избранного';

        toastStore.showToast(toastText);

        if (!success) throw new Error(message);

        await updateCount(visitorID);
        // TODO: синхронизирует только первые 100 товаров — при большем количестве Mindbox получит неполный вишлист
        await updateList(visitorID, 1, 100, true);

        return success;

      } finally {
        pendingRequests.delete(itemID);
      }
    })();

    pendingRequests.set(itemID, requestPromise);
    return requestPromise;
  };

  const add = (visitorID: number, itemID: number) => _executeFavoriteAction('add', visitorID, itemID);

  const remove = (visitorID: number, itemID: number) => _executeFavoriteAction('remove', visitorID, itemID);

  const updateList = async (
    visitorID: number,
    page: number = 1,
    count: number = 24,
    syncMindbox: boolean = false,
  ): Promise<void> => {
    const { data, success, message } = await $api.favorites.getFavorites<IProduct[]>(visitorID, { page, count });

    if (success && data) {
      visitorStore.setFavoriteItems(data.items);
      visitorStore.setFavoriteTotal(data.total);

      if (syncMindbox) {
        syncMindboxWishlist();
      }
    }

    if (!success) throw message;
  };

  const updateCount = async (visitorID: number): Promise<void> => {
    const { data, success, message } = await $api.favorites.getFavoritesCount(visitorID);

    if (success && data) {
      visitorStore.setFavoriteCount(data.count);
    }

    if (!success) throw new Error(message);
  };

  return {
    add,
    remove,
    updateList,
    updateCount,
  };
};
